import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Plus, Pencil, Trash2, ImageIcon, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/constants/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pill, StatusPill, DataTable, FilterTabs, SectionTitle, Field, Empty, fmtDate } from "@/components/dash";
import { CONTENT } from "@/pages/StaticPage";

/* ------------------------------------------------------------------ moderation */
export function ModerationPanel({ onChanged }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/moderation/products", { params: { status } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === "approve") await api.post(`/admin/products/${open.id}/approve`);
      else await api.post(`/admin/products/${open.id}/reject`, { note });
      toast.success(kind === "approve" ? "Product approved and live" : "Product rejected — seller notified");
      setOpen(null); setRejecting(false); setNote(""); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const c = data?.counts || {};
  return (
    <div data-testid="moderation-panel">
      <SectionTitle title="Product moderation" desc="New and edited seller listings stay hidden from customers until approved here." />
      <FilterTabs value={status} onChange={setStatus} options={[
        { key: "pending", label: "Awaiting review", count: c.pending }, { key: "approved", label: "Approved", count: c.approved },
        { key: "rejected", label: "Rejected", count: c.rejected }, { key: "all", label: "All" }]} />
      <DataTable testid="moderation-table" empty={status === "pending" ? "Nothing waiting for review 🎉" : "No products here."}
        head={["Product", "Seller", "Category", "Price", "Stock", "Added", "Status", "Actions"]}
        rows={(data?.items || []).map((p) => [
          <button className="flex items-center gap-2 text-left" onClick={() => { setOpen(p); setRejecting(false); setNote(""); }}>
            <img src={(p.images || [])[0]} alt="" className="h-10 w-10 rounded object-cover bg-muted shrink-0" />
            <span className="line-clamp-2 max-w-[200px] font-medium">{p.name}</span>
          </button>,
          p.vendor_name, p.category, formatINR(p.price), p.stock, fmtDate(p.created_at), <StatusPill status={p.moderation || "approved"} />,
          <Button size="sm" variant={p.moderation === "pending" ? "default" : "outline"} onClick={() => { setOpen(p); setRejecting(false); setNote(""); }} data-testid={`moderate-${p.id}`}>Review</Button>,
        ])} />

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="moderation-dialog">
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.name}</DialogTitle><DialogDescription>by {open.vendor_name} · {open.category}</DialogDescription></DialogHeader>
              {(open.images || []).length > 0
                ? <div className="flex gap-2 overflow-x-auto">{open.images.map((u, i) => <img key={i} src={u} alt="" className="h-40 rounded-lg border border-border object-contain bg-white" />)}</div>
                : <Empty icon={ImageIcon} title="No image provided" />}
              <p className="text-sm whitespace-pre-line">{open.description || <span className="text-muted-foreground">No description.</span>}</p>
              <div className="flex gap-6 text-sm"><span>Price <b>{formatINR(open.price)}</b></span><span>MRP <b>{formatINR(open.mrp)}</b></span><span>Stock <b>{open.stock}</b></span></div>
              {open.moderation_note && <p className="text-sm rounded-lg bg-red-50 border border-red-200 p-3 text-red-800"><b>Rejected:</b> {open.moderation_note}</p>}
              {rejecting && <Field label="Reason (the seller sees this)"><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Image is blurry / description missing material details" data-testid="product-reject-note" /></Field>}
              <DialogFooter>
                {!rejecting ? (
                  <>
                    {open.moderation !== "rejected" && <Button variant="outline" className="text-destructive" onClick={() => setRejecting(true)} data-testid="product-reject"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>}
                    {open.moderation !== "approved" && <Button disabled={busy} onClick={() => act("approve")} data-testid="product-approve"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>}
                  </>
                ) : (
                  <><Button variant="outline" onClick={() => setRejecting(false)}>Back</Button><Button variant="destructive" disabled={busy || note.trim().length < 5} onClick={() => act("reject")} data-testid="product-reject-confirm">Reject product</Button></>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ CMS */
export const BANNER_THEMES = {
  emerald: { label: "Emerald", cls: "from-[#04301b] via-[#0a4a27] to-[#1b6b24]" },
  forest: { label: "Forest", cls: "from-[#06301f] via-[#0f5a3c] to-[#1d8157]" },
  teal: { label: "Teal", cls: "from-[#072a26] via-[#0f4d46] to-[#1b7a6c]" },
  olive: { label: "Olive", cls: "from-[#17330f] via-[#2a5a17] to-[#4a8a22]" },
};
const emptyBanner = { title: "", eyebrow: "", cta: "Shop now", link: "/products", theme: "emerald", image_url: "", active: true, sort: 0 };

function BannersTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => api.get("/admin/cms/banners").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, sort: parseInt(form.sort, 10) || 0 };
      if (editId) await api.put(`/admin/cms/banners/${editId}`, body); else await api.post("/admin/cms/banners", body);
      toast.success("Banner saved — live on the homepage"); setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete banner “${b.title}”?`)) return;
    try { await api.delete(`/admin/cms/banners/${b.id}`); toast.success("Banner deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (b) => {
    try { await api.put(`/admin/cms/banners/${b.id}`, { ...b, active: !b.active }); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <SectionTitle title="Homepage banners" desc="When at least one banner is active, they replace the built-in slides on the homepage carousel. Delete or disable them all to go back to the default slides."
        action={<Button className="gap-1.5" onClick={() => { setForm(emptyBanner); setEditId(null); }} data-testid="add-banner"><Plus className="h-4 w-4" /> Add banner</Button>} />
      {items && items.length === 0 ? <Empty icon={ImageIcon} title="No custom banners" desc="The homepage is showing the default Zilkart slides." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {(items || []).map((b) => (
            <div key={b.id} className="bg-white rounded-xl border border-border overflow-hidden" data-testid={`banner-${b.id}`}>
              <div className={`h-28 bg-gradient-to-r ${BANNER_THEMES[b.theme]?.cls} text-white px-5 flex items-center justify-between gap-3 relative`}>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#e9c46a]">{b.eyebrow}</p>
                  <p className="font-display text-lg font-bold leading-tight line-clamp-2">{b.title}</p>
                </div>
                {b.image_url && <img src={b.image_url} alt="" className="h-20 w-20 rounded-lg object-cover bg-white/10 shrink-0" />}
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground min-w-0"><p className="truncate">→ {b.link}</p><p>Order #{b.sort}</p></div>
                <div className="flex items-center gap-1">
                  <Switch checked={b.active} onCheckedChange={() => toggle(b)} aria-label="Active" />
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ ...emptyBanner, ...b }); setEditId(b.id); }}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(b)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="banner-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit banner" : "New banner"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              <Field label="Headline"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={90} data-testid="banner-title" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Small label above"><Input value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} maxLength={50} /></Field>
                <Field label="Button text"><Input value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} maxLength={30} /></Field>
              </div>
              <Field label="Link" hint="Page on your site (e.g. /products?category=fashion) or a full https:// link"><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="banner-link" /></Field>
              <Field label="Image URL (optional)" hint="Shown on the right. Leave blank to show your products instead."><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Colour theme">
                  <Select value={form.theme} onValueChange={(v) => setForm({ ...form, theme: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(BANNER_THEMES).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Order (lowest first)"><Input type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: e.target.value })} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Show on homepage</label>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button type="submit" data-testid="banner-save">Save banner</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PagesTab() {
  const [slugs, setSlugs] = useState([]);
  const [saved, setSaved] = useState({});
  const [slug, setSlug] = useState("about");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const load = useCallback(() => api.get("/admin/cms/pages").then(({ data }) => {
    setSlugs(data.slugs); setSaved(Object.fromEntries(data.pages.map((p) => [p.slug, p])));
  }).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const src = saved[slug] || { title: CONTENT[slug]?.title || "", body: CONTENT[slug]?.body || [] };
    setTitle(src.title); setBody(src.body.join("\n\n"));
  }, [slug, saved]);

  const save = async () => {
    try {
      await api.put(`/admin/cms/pages/${slug}`, { title, body: body.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean) });
      toast.success("Page published"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Discard your edits and restore the default text?")) return;
    try { await api.delete(`/admin/cms/pages/${slug}`); toast.success("Restored default"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-3xl">
      <SectionTitle title="Site pages" desc="Edit the text of your About, Help, Privacy, Terms, Shipping and Refund pages. Separate paragraphs with a blank line." />
      <div className="flex flex-wrap gap-2 mb-4">
        {slugs.map((s) => (
          <button key={s} onClick={() => setSlug(s)} className={`px-3.5 py-1.5 rounded-full text-sm border capitalize ${slug === s ? "bg-primary text-white border-primary" : "bg-white border-border"}`} data-testid={`cms-page-${s}`}>
            {s}{saved[s] && <span className="ml-1.5 text-[10px] uppercase opacity-80">edited</span>}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-border p-5 space-y-4">
        <Field label="Page title"><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cms-title" /></Field>
        <Field label="Content"><Textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} data-testid="cms-body" /></Field>
        <div className="flex items-center justify-between gap-2">
          <a href={`/${slug}`} target="_blank" rel="noreferrer" className="text-sm text-primary font-semibold hover:underline">View live page ↗</a>
          <div className="flex gap-2">
            {saved[slug] && <Button variant="outline" onClick={reset}><RotateCcw className="h-4 w-4 mr-1" /> Restore default</Button>}
            <Button onClick={save} disabled={!title.trim() || !body.trim()} data-testid="cms-save">Publish changes</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CmsPanel() {
  const [tab, setTab] = useState("banners");
  return (
    <div data-testid="cms-panel">
      <div className="flex gap-2 mb-6 border-b border-border">
        {[["banners", "Banners"], ["pages", "Pages"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`} data-testid={`cms-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "banners" ? <BannersTab /> : <PagesTab />}
    </div>
  );
}
